import React from "react";
import { useTranslation } from "react-i18next";

import { AutocompleteOptions, SelectOptions } from "../assets/data/options";
import { Payment } from "../backend/generated/payment-model";
import { UpdateUserManageable } from "../store/user/types";
import { getInputDateFormat, isIterable } from "../utils/utility";
import {
  CommonAutocomplete,
  CommonCheckbox,
  CommonDatePicker,
  CommonPhoneInput,
  CommonSelect,
  CommonTextField,
  CountryAutocomplete,
} from "./index";

export const UserInfoField = ({
  id,
  values,
  errors,
  touched,
  setFieldValue,
  disableImmutable,
  className,
  disabled,
  noLabels,
  subkey,
  indexkey,
}: any) => {
  // Hooks
  const {
    t, i18n
  } = useTranslation();

  // Helpers
  const shouldDisable = (key: string) => {
    return disabled || disableImmutable && !Object.keys(new UpdateUserManageable()).includes(key);
  };

  const setSubFieldValue = (
    id: string, subkey: any, value: any, index?: number
  ) => {
    if (subkey !== undefined) {
      let field = isIterable(values[id]) ? [...values[id]] : { ...values[id] };
      if (index !== undefined) {
        field[index][subkey] = value;
      } else {
        field[subkey] = value;
      }
      setFieldValue(
        id,
        field
      );
    }
  };

  const getCommonDatePickerWrapperClass = (key: string) => {
    const isValidationFailed = errors && touched && errors[key] && touched[key];
    const isEmpty = values[key] ? false : true;
    return (
      "common-date-picker" +
      (isValidationFailed
        ? "--error"
        : shouldDisable(key)
          ? "--disabled"
          : "") +
      " common-date-picker" +
      (isEmpty ? "--empty" : "")
    );
  };

  const getPlaceholder = (id: string) => {
    if (noLabels) {
      return null;
    }

    switch (id) {
      case "address":
        return t("Address");
      case "availableCash":
        return t("Available cash");
      case "addressCity":
        return t("City of residence");
      case "addressCountry":
        return t("Address country");
      case "addressZipcode":
        return t("Zipcode");
      case "birthCity":
        return t("Birth city");
      case "bondsCertificates":
        return t("Select...");
      case "cellphone":
        return t("+49 12 345678");
      case "citizenship":
        return t("Citizenship");
      case "company":
        switch (subkey) {
          case "cocNumber":
            return t("CoC");
          case "companyName":
            return t("Company Name");
          default:
            return null;
        }
      case "confirmPassword":
        return t("Confirm password");
      case "defaultPayment":
        return t("Select...");
      case "email":
        return t("email@mail.de");
      case "employment":
        return t("Current occupation");
      case "experience":
        switch (subkey) {
          case "level":
            return t("Select...");
          case "transactionsPerYear":
            return t("Transactions/Year");
          case "volumePerYear":
            return t("Volume/Year");
          default:
            return null;
        }
      case "firstName":
        return t("First name");
      case "gender":
        return t("Select...");
      case "highestEducation":
        return t("Highest education");
      case "idDocumentCountry":
        return t("ID document country");
      case "idDocumentNumber":
        return t("ID document number");
      case "idDocumentType":
        return t("ID type");
      case "lastName":
        return t("Last name");
      case "monthlySalary":
        return t("Monthly salary");
      case "occupationGroup":
        return t("Occupation group");
      case "passportIssuingAgency":
        return t("Issuing agency");
      case "password":
        return t("Password");
      case "politicallyExposed":
        return t("Select...");
      case "prevEmployment":
        return t("Previous occupation");
      case "taxNumber":
        return t("Tax number");
      default:
        return null;
    }
  }

  const getInitialFocusedDate = (id: string) => {
    switch (id) {
      case "birthDate":
        let initFocusDate = new Date();
        initFocusDate.setFullYear(initFocusDate.getFullYear() - 50);
        return initFocusDate;
      default:
        return undefined;
    }
  }

  const getLabel = (id: string) => {
    if (noLabels) {
      return null;
    }

    switch (id) {
      case "birthDate":
        return t("Birth date");
      case "cellphone":
        return t("Cellphone number");
      case "defaultPayment":
        return t("Default payment method");
      case "email":
        return t("Email");
      case "experience":
        switch (subkey) {
          case "level":
            return t("Experience");
          default:
            return null;
        }
      case "gender":
        return t("Gender");
      case "notProvidingExperience":
        return t("I don't want to give any details");
      case "notUSTax":
        return t("I am not a US tax resident");
      case "politicallyExposed":
        return t("Are you a politically exponated person?");
      case "skipIdNow":
        return t("Skip IDNow");
      default:
        return null;
    }
  }

  const getOptions = (id: string) => {
    switch (id) {
      case "defaultPayment":
        return !values.payments ?
          [] :
          values.payments
            .map((account: Payment) => {
              return {
                label: account.IBAN,
                value: account.id
              };
            });
      case "experience":
        switch (subkey) {
          case "level":
            return SelectOptions().experience;
          default:
            return null;
        }
      case "gender":
        return SelectOptions().gender;
      case "highestEducation":
        return AutocompleteOptions().educationLevel;
      case "idDocumentType":
        return SelectOptions().idDocumentType;
      case "occupationGroup":
        return AutocompleteOptions().occupationGroup;
      case "politicallyExposed":
        return SelectOptions().yesNo;
      default:
        return null;
    }
  }

  const getType = (id: string) => {
    switch (id) {
      case "availableCash":
      case "monthlySalary":
      case "experience":
        switch (subkey) {
          case "transactionsPerYear":
          case "volumePerYear":
            return "number";
          default:
            return undefined;
        }
      case "confirmPassword":
      case "password":
        return "password";
      default:
        return undefined;
    }
  }

  const getMaxDate = (id: string) => {
    switch (id) {
      case "birthDate":
        let maxDate = new Date();
        maxDate.setFullYear(maxDate.getFullYear() - 18);
        return maxDate;
      default:
        return new Date("2100-01-01");
    }
  }

  const getProcessedValues = (
    id: string, input: any
  ) => {
    switch (id) {
      case "company":
        return {
          ...input,
          [id]: !!input[id] && subkey !== undefined ? input[id][subkey] : undefined
        }
      case "experience":
        const index = !!input[id] && indexkey !== undefined ? Array.from(values[id]).findIndex((experience: any) => experience.type === indexkey) : -1;
        return {
          ...input,
          [id]: index >= 0 && input[id][index] !== undefined && subkey !== undefined ? input[id][index][subkey] : undefined
        }
      default:
        return input;
    }
  }

  const getSetFieldValueWrapper = (id: string) => {
    switch (id) {
      case "availableCash":
      case "monthlySalary":
        return (
          id: string, value: number
        ) => setFieldValue(
          id,
          Math.round(value)
        );
      case "company":
        return (
          id: string, value: number
        ) => setSubFieldValue(
          id,
          subkey,
          value
        );
      case "experience":
        let preSetFormat = (value: any) => value;
        switch (subkey) {
          case "transactionsPerYear":
          case "volumePerYear":
            preSetFormat = (value: any) => Math.round(value);
            break;
          default:
            break;
        }

        const index = !!values[id] && indexkey !== undefined ? Array.from(values[id]).findIndex((experience: any) => experience.type === indexkey) : -1;

        return (
          id: string, value: number
        ) => setSubFieldValue(
          id,
          subkey,
          preSetFormat(value),
          index
        );
      default:
        return setFieldValue;
    }
  }

  const shouldDisableFuture = (id: string) => {
    switch (id) {
      default:
        return false;
    }
  }

  const shouldDisablePast = (id: string) => {
    switch (id) {
      default:
        return false;
    }
  }

  // Options
  const disableFuture = shouldDisableFuture(id);
  const disablePast = shouldDisablePast(id);
  const initialFocusedDate = getInitialFocusedDate(id);
  const label = getLabel(id);
  const maxDate = getMaxDate(id);
  const placeholder = getPlaceholder(id);
  const options = getOptions(id);
  const type = getType(id);
  const setFieldValueWrapper = getSetFieldValueWrapper(id);
  const processedErrors = getProcessedValues(
    id,
    errors
  );
  const processedValues = getProcessedValues(
    id,
    values
  );

  switch (id) {
    case "experience":
      switch (subkey) {
        case "level":
          return (
            <CommonSelect
              disabled={shouldDisable(id)}
              id={id}
              label={label}
              placeholder={placeholder}
              values={processedValues}
              errors={processedErrors}
              touched={touched}
              setFieldValue={setFieldValueWrapper}
              options={options}
              className={className}
            />
          );
        case "transactionsPerYear":
        case "volumePerYear":
          return (
            <CommonTextField
              disabled={shouldDisable(id)}
              id={id}
              type={type}
              label={label}
              placeholder={placeholder}
              values={processedValues}
              errors={processedErrors}
              touched={touched}
              setFieldValue={setFieldValueWrapper}
              className={className}
            />
          );
        default:
          return <></>;
      }
    case "address":
    case "availableCash":
    case "addressCity":
    case "addressZipcode":
    case "birthCity":
    case "company":
    case "confirmPassword":
    case "email":
    case "employment":
    case "firstName":
    case "idDocumentNumber":
    case "lastName":
    case "monthlySalary":
    case "passportIssuingAgency":
    case "password":
    case "prevEmployment":
    case "taxNumber":
      return (
        <CommonTextField
          disabled={shouldDisable(id)}
          id={id}
          type={type}
          label={label}
          placeholder={placeholder}
          values={processedValues}
          errors={processedErrors}
          touched={touched}
          setFieldValue={setFieldValueWrapper}
          className={className}
        />
      );
    case "defaultPayment":
    case "gender":
    case "idDocumentType":
    case "politicallyExposed":
      return (
        <CommonSelect
          disabled={shouldDisable(id)}
          id={id}
          label={label}
          placeholder={placeholder}
          values={processedValues}
          errors={processedErrors}
          touched={touched}
          setFieldValue={setFieldValueWrapper}
          options={options}
          className={className}
        />
      );
    case "cellphone":
      return (
        <CommonPhoneInput
          disabled={shouldDisable(id)}
          type="tel"
          id={id}
          placeholder={placeholder}
          label={label}
          values={values}
          errors={errors}
          touched={touched}
          setFieldValue={setFieldValue}
          className={className}
        />
      );
    case "birthDate":
      return (
        <CommonDatePicker
          className={className + " " + getCommonDatePickerWrapperClass(id)}
          disabled={shouldDisable(id)}
          id={id}
          values={values}
          errors={errors}
          touched={touched}
          label={label}
          setFieldValue={setFieldValue}
          maxDate={maxDate}
          disableFuture={disableFuture}
          disablePast={disablePast}
          openTo="year"
          views={["year", "month", "date"]}
          format={getInputDateFormat(i18n.language)}
          initialFocusedDate={initialFocusedDate}
        />
      );
    case "addressCountry":
    case "citizenship":
    case "idDocumentCountry":
      return (
        <CountryAutocomplete
          disabled={shouldDisable(id)}
          id={id}
          placeholder={placeholder}
          values={values}
          errors={errors}
          touched={touched}
          setFieldValue={setFieldValue}
          options={AutocompleteOptions().countries}
          className={className}
        />
      );
    case "highestEducation":
    case "occupationGroup":
      return (
        <CommonAutocomplete
          disabled={shouldDisable(id)}
          id={id}
          placeholder={placeholder}
          values={values}
          errors={errors}
          touched={touched}
          setFieldValue={setFieldValue}
          options={options}
          className={className}
        />
      );
    case "notProvidingExperience":
    case "notUSTax":
    case "skipIdNow":
      return (
        <CommonCheckbox
          id={id}
          disabled={shouldDisable(id)}
          values={values}
          errors={errors}
          touched={touched}
          setFieldValue={setFieldValue}
          label={label}
          className={className}
        />
      );
    default:
      return <></>;
  }
};
